import { fetchWithAuth } from './api';
import { okJson } from './httpError';
import { normalizePhotoForUpload } from '../utils/uploadPhoto';

export interface OcorrenciaPerson {
  id: string;
  first_name: string;
  last_name: string;
  identity_photo_url?: string | null;
  profile_picture_url?: string | null;
}

export interface OcorrenciaComment {
  id: string;
  ocorrencia_id: string;
  person_id?: string;
  person?: OcorrenciaPerson;
  created_by_user_id?: string;
  created_by_name?: string;
  content: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface OcorrenciaUnit {
  id: string;
  number: string;
  block?: { id: string; name: string };
  street?: { id: string; name: string };
  street_number?: string;
}

export interface Ocorrencia {
  id: string;
  customer_id: string;
  person_id?: string;
  person?: OcorrenciaPerson;
  created_by_user_id?: string;
  created_by_name?: string;
  unit_id?: string;
  unit?: OcorrenciaUnit;
  subject_person_id?: string;
  subject_person?: OcorrenciaPerson;
  title: string;
  description: string;
  photo_url?: string;
  status: 'open' | 'resolved';
  is_active: boolean;
  resolved_at?: string;
  resolved_by_id?: string;
  created_at: string;
  updated_at: string;
  comments?: OcorrenciaComment[];
}

export interface CreateOcorrenciaPayload {
  title: string;
  description: string;
  photo_url?: string;
  unit_id?: string;
  subject_person_id?: string;
}

export interface UploadOcorrenciaPhotoResponse {
  success: boolean;
  photo_url: string;
}

export interface AddCommentPayload {
  content: string;
}

export const ocorrenciasService = {
  async listOcorrencias(customerId: string, status?: string): Promise<Ocorrencia[]> {
    const params = status ? `?status=${status}` : '';
    const res = await fetchWithAuth(`/api/v1/customers/${customerId}/ocorrencias${params}`);
    if (!res.ok) throw new Error(`Failed to fetch ocorrencias: ${res.status}`);
    const data = await res.json();
    return data.ocorrencias ?? [];
  },

  async uploadPhoto(customerId: string, file: File): Promise<UploadOcorrenciaPhotoResponse> {
    const formData = new FormData();
    formData.append('photo', await normalizePhotoForUpload(file));
    const res = await fetchWithAuth(`/api/v1/customers/${customerId}/ocorrencias/photo`, {
      method: 'POST',
      body: formData,
    });
    const json = await okJson<any>(res, 'Erro ao enviar a foto');
    return json;
  },

  async createOcorrencia(customerId: string, payload: CreateOcorrenciaPayload): Promise<Ocorrencia> {
    const res = await fetchWithAuth(`/api/v1/customers/${customerId}/ocorrencias`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`Failed to create ocorrencia: ${res.status}`);
    const data = await res.json();
    return data.ocorrencia;
  },

  async resolveOcorrencia(customerId: string, ocId: string): Promise<void> {
    const res = await fetchWithAuth(`/api/v1/customers/${customerId}/ocorrencias/${ocId}/resolve`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    if (!res.ok) throw new Error(`Failed to resolve ocorrencia: ${res.status}`);
  },

  async deleteOcorrencia(customerId: string, ocId: string): Promise<void> {
    const res = await fetchWithAuth(`/api/v1/customers/${customerId}/ocorrencias/${ocId}`, {
      method: 'DELETE',
    });
    if (!res.ok) throw new Error(`Failed to delete ocorrencia: ${res.status}`);
  },

  async listComments(customerId: string, ocId: string): Promise<OcorrenciaComment[]> {
    const res = await fetchWithAuth(`/api/v1/customers/${customerId}/ocorrencias/${ocId}/comments`);
    if (!res.ok) throw new Error(`Failed to fetch comments: ${res.status}`);
    const data = await res.json();
    return data.comments ?? [];
  },

  async addComment(customerId: string, ocId: string, payload: AddCommentPayload): Promise<OcorrenciaComment> {
    const res = await fetchWithAuth(`/api/v1/customers/${customerId}/ocorrencias/${ocId}/comments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`Failed to add comment: ${res.status}`);
    const data = await res.json();
    return data.comment;
  },

  async deleteComment(customerId: string, ocId: string, commentId: string): Promise<void> {
    const res = await fetchWithAuth(
      `/api/v1/customers/${customerId}/ocorrencias/${ocId}/comments/${commentId}`,
      { method: 'DELETE' }
    );
    if (!res.ok) throw new Error(`Failed to delete comment: ${res.status}`);
  },
};
